import { useEffect } from 'react';
import { Spinner } from 'react-bootstrap';
import { Typography } from '@mui/material';
import { getUserMetaDataNonPIUserSettings, loginUser } from '../../shared/functions/userFunctions';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import * as UserActions from './userSlice';
import jwt_decode from 'jwt-decode';
import { startRefreshTokenTimer } from './refreshToken';
import { startCloudfrontRefreshTimer } from '../../shared/functions/refreshCloudfrontCookies';
import { userPrivilegeMap } from '../../shared/constants/constants';
import { RouteInformation } from '../../shared/constants/RouteInformation';
import { validateTermsOfServiceAgreement } from './validateTermsOfServiceAgreement';
import { getAddonPageAccessControl } from '../../shared/requests/addonPageAccessControlRequests';
import { getUserBroadcastPopup } from '../../shared/requests/broadcastPopupRequests';

function LoginLoading() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);

  //submit login call to api
  useEffect(() => {
    const loginAsync = async () => {
      const queryParams = new URLSearchParams(window.location.search);
      const code = queryParams.get('code');

      dispatch(UserActions.setIsFetching());

      try {
        const resp = await loginUser({ code: code });

        if (resp) {
          //update user info in redux if successful api call
          const tokens = JSON.parse(resp.tokens);
          const id_token = jwt_decode(tokens.id_token);
          const userInfo = id_token.mainUserAttributes;

          dispatch(UserActions.setIdToken(tokens.id_token));
          dispatch(UserActions.setAccessToken(tokens.access_token));

          const { nonPIUserSettings } = await getUserMetaDataNonPIUserSettings(userInfo.sub);

          const termsOfServiceAgreed = userInfo['custom:termsOfServiceAgreed'] || null;
          const userTenantId = userInfo['custom:tenantId'] || '';
          const addonPageAccessControls = await getAddonPageAccessControl();
          await validateTermsOfServiceAgreement(termsOfServiceAgreed, dispatch);
          const userBroadcastPopup = await getUserBroadcastPopup();

          dispatch(
            UserActions.setUserInfo({
              username: userInfo.username,
              email: userInfo.email,
              id: userInfo.sub,
              privilege: userPrivilegeMap[userInfo['cognito:groups'][0]] || 0,
              name: userInfo.name,
              avatar: userInfo.picture,
              nonPIUserSettings: nonPIUserSettings,
              termsOfServiceAgreed: termsOfServiceAgreed,
              tenantId: userTenantId,
              phone: userInfo.phone || 'Not Configured',
              preferredMfaMethod: 'Not Configured',
            })
          );
          dispatch(UserActions.setIsAuthenticated());
          dispatch(UserActions.setUserAddonPageAccessControls({ ...addonPageAccessControls }));
          dispatch(UserActions.setUserBroadcastPopup({ ...userBroadcastPopup }));

          startRefreshTokenTimer();
          startCloudfrontRefreshTimer();
        } else {
          //fail to login. redirect to home page.
          console.error(resp);
          window.location.replace(process.env.REACT_APP_COGNITO_URL);
        }
      } catch (err) {
        console.error(err);
        window.location.replace(process.env.REACT_APP_COGNITO_URL);
      }
    };

    loginAsync();
  }, [dispatch]);

  useEffect(() => {
    if (user.isAuthenticated) {
      const redirectUrl = localStorage.getItem('redirectUrlAfterLogin');

      if (redirectUrl) {
        localStorage.removeItem('redirectUrlAfterLogin');
        navigate(redirectUrl);
      } else {
        navigate(RouteInformation.serviceLog.path);
      }
    }
  }, [user, navigate]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        position: 'fixed',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
      }}
    >
      <Spinner animation="border"></Spinner>
      <Typography>Please wait, logging you in to Broadsight Tracker</Typography>
    </div>
  );
}

export default LoginLoading;
